import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { domainRequestsAPI, runtimeAPI } from '@hpanel/web-common/api/client'
import type { CatalogImage } from '@hpanel/web-common/api/types'
import { ErrorBox, cardClass, inputClass, labelClass, toMessage } from '../../components/sites/siteUi'

/**
 * Request a new site (/client/sites/request): a domain and an optional image.
 * An administrator approves it; until then it shows as pending on the Sites
 * page. Its own page (CLAUDE.md §15).
 */
export const RequestSitePage: React.FC = () => {
  const navigate = useNavigate()
  const [images, setImages] = useState<CatalogImage[]>([])
  const [name, setName] = useState('')
  const [image, setImage] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    // The catalog only offers choices; the page works without it (the
    // administrator then picks the default image).
    runtimeAPI
      .listAvailable('docker')
      .then(setImages)
      .catch((e: unknown) => {
        // eslint-disable-next-line no-console
        console.error('[sites] image list unavailable', e)
      })
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    const cleanName = name.trim().toLowerCase()
    try {
      await domainRequestsAPI.submit({ kind: 'slot', name: cleanName, ...(image ? { image } : {}) })
      navigate('/client/sites', {
        state: { flash: `Requested ${cleanName}. An administrator will review it.` },
      })
    } catch (err: unknown) {
      setError(toMessage('request site', err, 'Failed to send the request'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div data-testid="site-request-page" className="space-y-6 max-w-3xl">
      <div>
        <Link to="/client/sites" className="text-sm text-primary-600 hover:underline">
          ← Sites
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">Request a site</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          An administrator reviews the request. Once approved, the site appears on the Sites page.
        </p>
      </div>

      <ErrorBox message={error} testId="site-request-error" />

      <form onSubmit={handleSubmit} className={cardClass}>
        <div>
          <label htmlFor="site-request-name" className={labelClass}>Domain</label>
          <input
            id="site-request-name"
            data-testid="site-request-name"
            type="text"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="example.com"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="site-request-image" className={labelClass}>Image</label>
          <select
            id="site-request-image"
            data-testid="site-request-image"
            value={image}
            onChange={(e) => setImage(e.target.value)}
            className={inputClass}
          >
            <option value="">Default (chosen by the administrator)</option>
            {images.map((i) => (
              <option key={i.id} value={i.oci_reference}>
                {i.name} ({i.oci_reference})
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          data-testid="site-request-submit"
          disabled={saving || !name.trim()}
          className="btn-primary disabled:opacity-60"
        >
          {saving ? 'Sending…' : 'Request site'}
        </button>
      </form>
    </div>
  )
}
